// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, X, Award, FilePlus, Pencil } from 'lucide-react';

import {
  Badge,
  Card,
  Input,
  Button,
  InfoBanner,
  Select,
  WorkflowDateField,
  ConfirmDialog,
  workflowTableIconButtonEditClass,
  workflowTableIconButtonDangerClass,
} from '../../components/WorkflowFormPrimitives';
import { fmt } from '../../utils/formatCurrency';
import { useCompanyContext } from '../../context/CompanyContext';
import { PARACHUTE_STEP_INFO } from './parachuteCopy';
import { formatIsoYmdForDisplay } from '../../../../utils/isoDateDisplay';
import { calculateEquityAwardQa24cValue } from '../../utils/qa24cEquity';

export const EquityAwards = ({ formData, setFormData }) => {
  const { currentAnalysisId } = useCompanyContext();
  const individuals = formData.individuals || [];
  const uniqueNames = Array.from(new Set(individuals.map((i: { name: string }) => i.name)));
  const uniqueIndividuals = uniqueNames.map((name) => individuals.find((i) => i.name === name));
  const [data, setData] = useState(formData.equityData || {});
  const lastIncomingEquityJson = useRef(null);

  useEffect(() => {
    lastIncomingEquityJson.current = null;
  }, [currentAnalysisId]);

  useEffect(() => {
    const raw = formData.equityData || {};
    const incoming = raw && typeof raw === 'object' ? raw : {};
    const json = JSON.stringify(incoming);
    if (lastIncomingEquityJson.current !== json) {
      lastIncomingEquityJson.current = json;
      setData(incoming);
    }
  }, [formData.equityData]);

  const fileInputRef = React.useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAwardId, setEditingAwardId] = useState(null);
  const [editingIndId, setEditingIndId] = useState(null);
  const [selectedIndividual, setSelectedIndividual] = useState('');
  const emptyAward = {
    type: 'option',
    vestingType: 'time',
    performanceConditionMet: 'no',
    totalShares: '',
    exercisePrice: '',
    purchasePrice: '',
    grantDate: '',
    vestingSchedule: 'monthly-4yr-1cliff',
    customVestingMonths: '',
    customCliffMonths: '',
    accelerationType: 'full',
    accelerationPercentage: '100',
    treatmentOnCIC: 'cashedOut',
    useFullValue: false,
  };
  const [awardForm, setAwardForm] = useState(emptyAward);
  const [awardDeleteConfirm, setAwardDeleteConfirm] = useState(null);

  const VESTING_SCHEDULES = [
    { id: 'monthly-4yr-1cliff', label: 'Monthly / 4yr / 1yr cliff', months: 48, cliffMonths: 12 },
    { id: 'quarterly-4yr-1cliff', label: 'Quarterly / 4yr / 1yr cliff', months: 48, cliffMonths: 12 },
    { id: 'annual-4yr', label: 'Annual / 4yr / no cliff', months: 48, cliffMonths: 0 },
    { id: 'annual-4yr-1cliff', label: 'Annual / 4yr / 1yr cliff', months: 48, cliffMonths: 12 },
    { id: 'monthly-3yr-1cliff', label: 'Monthly / 3yr / 1yr cliff', months: 36, cliffMonths: 12 },
    { id: 'quarterly-3yr-1cliff', label: 'Quarterly / 3yr / 1yr cliff', months: 36, cliffMonths: 12 },
    { id: 'annual-3yr', label: 'Annual / 3yr / no cliff', months: 36, cliffMonths: 0 },
    { id: 'monthly-4yr', label: 'Monthly / 4yr / no cliff', months: 48, cliffMonths: 0 },
    { id: 'quarterly-4yr', label: 'Quarterly / 4yr / no cliff', months: 48, cliffMonths: 0 },
    { id: '2yr-cliff', label: '2yr cliff (100% at 2yr)', months: 24, cliffMonths: 24 },
    { id: 'custom', label: 'Custom', months: 0, cliffMonths: 0 },
  ];

  const openAddModal = () => {
    setEditMode(false);
    setEditingAwardId(null);
    setEditingIndId(null);
    setSelectedIndividual('');
    setAwardForm(emptyAward);
    setShowModal(true);
  };

  const openEditModal = (indId, award) => {
    setEditMode(true);
    setEditingAwardId(award.id);
    setEditingIndId(indId);
    setSelectedIndividual(indId);
    setAwardForm({ ...award });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingAwardId(null);
    setEditingIndId(null);
  };

  const saveAward = () => {
    if (!selectedIndividual) return;
    if (editMode && editingAwardId) {
      // Update existing award
      setData((p) => ({
        ...p,
        [editingIndId]: {
          ...p[editingIndId],
          awards: (p[editingIndId]?.awards || []).map((a) =>
            a.id === editingAwardId ? { ...awardForm, id: editingAwardId } : a
          ),
        },
      }));
    } else {
      // Add new award
      setData((p) => {
        const indData = p[selectedIndividual] || { awards: [] };
        const awards = [...(indData.awards || []), { ...awardForm, id: Date.now() }];
        return { ...p, [selectedIndividual]: { ...indData, awards } };
      });
    }
    closeModal();
  };

  const removeAward = (indId, awardId) => {
    setData((p) => ({
      ...p,
      [indId]: { ...p[indId], awards: (p[indId]?.awards || []).filter((a) => a.id !== awardId) },
    }));
  };

  const requestRemoveAward = (indId, awardId, individualName, award) => {
    const typeLabel = award.type === 'option' ? 'Option' : award.type === 'rsu' ? 'RSU' : 'RSA';
    setAwardDeleteConfirm({
      indId,
      awardId,
      individualName,
      typeLabel,
      grantDate: formatGrantDate(award.grantDate),
      sharesLine: award.totalShares ? `${Number(award.totalShares).toLocaleString()} shares` : null,
    });
  };

  const confirmRemoveAward = () => {
    if (!awardDeleteConfirm) return;
    removeAward(awardDeleteConfirm.indId, awardDeleteConfirm.awardId);
    setAwardDeleteConfirm(null);
  };
  useEffect(() => {
    setFormData((p) => ({ ...p, equityData: data }));
  }, [data]);
  const getAwardCount = () =>
    (Object.values(data) as { awards?: unknown[] }[]).reduce((sum, d) => sum + (d.awards?.length || 0), 0);

  const formatGrantDate = (d) => formatIsoYmdForDisplay(d);

  const getVestingDetailsEquity = (award) => {
    const s = VESTING_SCHEDULES.find((v) => v.id === award.vestingSchedule);
    if (award.vestingSchedule === 'custom')
      return { months: +award.customVestingMonths || 0, cliffMonths: +award.customCliffMonths || 0 };
    return s ? { months: s.months, cliffMonths: s.cliffMonths } : { months: 48, cliffMonths: 12 };
  };

  const getAwardVestingAndValue = (award) => {
    const closingDate = formData.closingDate;
    const dealPrice = +formData.dealPrice || 0;
    const afr = (+formData.afr || 5) / 100;
    const discountRateForPV = 1.2 * afr;
    const normalizedAward =
      award?.vestingSchedule === 'custom' && getVestingDetailsEquity(award)
        ? {
            ...award,
            customVestingMonths: String(getVestingDetailsEquity(award).months || ''),
            customCliffMonths: String(getVestingDetailsEquity(award).cliffMonths || ''),
          }
        : award;
    return calculateEquityAwardQa24cValue({
      award: normalizedAward,
      closingDate,
      dealPrice,
      discountRateForPV,
    });
  };

  const toggleUseFullValue = (indId, awardId) => {
    setData((p) => ({
      ...p,
      [indId]: {
        ...p[indId],
        awards: (p[indId]?.awards || []).map((a) => (a.id === awardId ? { ...a, useFullValue: !a.useFullValue } : a)),
      },
    }));
  };

  const handleImportCsv = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result || '');
      if (!text.trim()) return;
      const rows = text.split(/\r?\n/).filter((r) => r.trim());
      if (rows.length < 2) return;

      const headers = rows[0].split(',').map((h) => h.trim().toLowerCase());
      const findIndex = (...names: string[]) => {
        for (const n of names) {
          const idx = headers.indexOf(n.toLowerCase());
          if (idx !== -1) return idx;
        }
        return -1;
      };

      const idxName = findIndex('individual', 'name', 'holder', 'security_holder', 'employee');
      const idxType = findIndex('type', 'security_type');
      const idxShares = findIndex('total_shares', 'quantity', 'shares');
      const idxExercise = findIndex('exercise_price', 'strike_price');
      const idxPurchase = findIndex('purchase_price');
      const idxGrantDate = findIndex('grant_date', 'grant_date(utc)');

      const importedByInd: Record<string, unknown[]> = {};

      for (let i = 1; i < rows.length; i += 1) {
        const cols = rows[i].split(',');
        if (!cols.length) continue;
        const rawName = idxName >= 0 ? cols[idxName].trim() : '';
        if (!rawName) continue;

        const match = uniqueIndividuals.find((ind) => (ind?.name || '').toLowerCase() === rawName.toLowerCase());
        if (!match) continue;

        const indId = match.id;
        const typeRaw = idxType >= 0 ? cols[idxType].trim().toLowerCase() : 'option';
        const type = typeRaw.includes('rsu')
          ? 'rsu'
          : typeRaw.includes('restricted') && !typeRaw.includes('unit')
            ? 'restricted_stock'
            : 'option';
        const totalShares = idxShares >= 0 ? cols[idxShares].replace(/[^0-9.]/g, '') : '';
        const exercisePrice = idxExercise >= 0 ? cols[idxExercise].replace(/[^0-9.]/g, '') : '';
        const purchasePrice = idxPurchase >= 0 ? cols[idxPurchase].replace(/[^0-9.]/g, '') : '';
        const grantDate = idxGrantDate >= 0 ? cols[idxGrantDate].trim() : '';

        const award = {
          ...emptyAward,
          type,
          totalShares,
          exercisePrice,
          purchasePrice,
          grantDate,
          id: Date.now() + i,
        };

        if (!importedByInd[indId]) importedByInd[indId] = [];
        importedByInd[indId].push(award);
      }

      if (Object.keys(importedByInd).length === 0) return;

      setData((prev) => {
        const next = { ...prev };
        Object.entries(importedByInd).forEach(([indId, awards]) => {
          const current = next[indId] || { awards: [] };
          next[indId] = { ...current, awards: [...(current.awards || []), ...(awards as unknown[])] };
        });
        return next;
      });
    };
    reader.readAsText(file);
    // reset so same file can be chosen again if needed
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  return (
    <div className="space-y-4">
      <InfoBanner showIcon>
        <p className="text-xs leading-relaxed">
          <strong className="font-semibold">Equity Awards.</strong> {PARACHUTE_STEP_INFO.equity}
        </p>
      </InfoBanner>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-600">{getAwardCount()} award(s) added</div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={openAddModal}>
            <Plus size={16} className="mr-1 shrink-0" aria-hidden /> Add Award
          </Button>
          <Button variant="secondary" size="sm" type="button" onClick={() => fileInputRef.current?.click()}>
            <FilePlus size={16} className="mr-1 shrink-0" aria-hidden /> Import CSV
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImportCsv} className="hidden" />
        </div>
      </div>
      {uniqueIndividuals.map((ind) => {
        const awards = data[ind.id]?.awards || [];
        if (awards.length === 0) return null;
        return (
          <Card key={ind.id} padding={false}>
            <div className="px-4 pt-4 pb-2 border-b border-gray-100">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h4 className="text-xs font-semibold text-gray-900">{ind.name}</h4>
                {ind.title ? <span className="text-xs text-gray-500">{ind.title}</span> : null}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                {awards.length} equity award{awards.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[1320px] text-center text-xs">
                <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 shadow-[1px_0_0_0_rgba(15,23,42,0.06)]">
                      Type
                    </th>
                    <th className="px-3 py-2">Total Shares</th>
                    <th className="px-3 py-2">Exercise Price</th>
                    <th className="px-3 py-2">Purchase Price</th>
                    <th className="px-3 py-2">Grant Date</th>
                    <th className="px-3 py-2 text-left">Vesting Schedule</th>
                    <th className="px-3 py-2">Accel Type</th>
                    <th className="px-3 py-2">Accel %</th>
                    <th className="px-3 py-2">CIC Treatment</th>
                    <th className="px-3 py-2">Shares Vested Prior to CIC</th>
                    <th className="px-3 py-2">Shares Vesting Upon CIC</th>
                    <th className="px-3 py-2">Valuation Method</th>
                    <th className="sticky right-[104px] z-20 bg-gray-50 px-3 py-2 shadow-[-1px_0_0_0_rgba(15,23,42,0.06)]">
                      Parachute Value
                    </th>
                    <th className="sticky right-0 z-20 w-[104px] min-w-[104px] bg-gray-50 px-2 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                  {awards.map((award) => {
                    const vest = getAwardVestingAndValue(award);
                    return (
                      <tr key={award.id} className="group bg-white hover:bg-gray-50">
                        <td className="sticky left-0 z-10 bg-white px-3 py-2 group-hover:bg-gray-50 shadow-[1px_0_0_0_rgba(15,23,42,0.06)]">
                          <Badge
                            size="table"
                            variant={
                              award.type === 'option'
                                ? 'equityOption'
                                : award.type === 'rsu'
                                  ? 'equityRsu'
                                  : 'equityRsa'
                            }
                          >
                            {award.type === 'option' ? 'Option' : award.type === 'rsu' ? 'RSU' : 'RSA'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {award.totalShares ? Number(award.totalShares).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2">{award.exercisePrice ? `$${award.exercisePrice}` : '—'}</td>
                        <td className="px-3 py-2">{award.purchasePrice ? `$${award.purchasePrice}` : '—'}</td>
                        <td className="px-3 py-2">{formatGrantDate(award.grantDate)}</td>
                        <td className="px-3 py-2 text-left">
                          {VESTING_SCHEDULES.find((v) => v.id === award.vestingSchedule)?.label || 'Custom'}
                        </td>
                        <td className="px-3 py-2">
                          {award.accelerationType === 'full'
                            ? 'Full'
                            : award.accelerationType === 'partial'
                              ? 'Partial'
                              : 'None'}
                        </td>
                        <td className="px-3 py-2">
                          {award.accelerationType === 'partial' ? `${award.accelerationPercentage || '0'}%` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {award.treatmentOnCIC === 'rolledOver'
                            ? 'Rolled over'
                            : award.treatmentOnCIC === 'assumed'
                              ? 'Assumed'
                              : 'Cashed out'}
                        </td>
                        <td className="px-3 py-2">{vest.sharesVestedPrior.toLocaleString()}</td>
                        <td className="px-3 py-2">{vest.sharesVestingUponCIC.toLocaleString()}</td>
                        <td className="px-3 py-2">
                          {vest.fullyVested ? (
                            <span className="text-gray-400">—</span>
                          ) : vest.qualifies24c ? (
                            <span className="inline-flex items-center justify-center gap-1.5 flex-wrap">
                              <span>{award.useFullValue ? 'Full value' : '24(c)'}</span>
                              <button
                                type="button"
                                onClick={() => toggleUseFullValue(ind.id, award.id)}
                                className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                              >
                                {award.useFullValue ? 'Use 24(c)' : 'Use full value'}
                              </button>
                            </span>
                          ) : (
                            <span>Full value</span>
                          )}
                        </td>
                        <td className="sticky right-[104px] z-10 bg-white px-3 py-2 font-semibold text-gray-900 group-hover:bg-gray-50 shadow-[-1px_0_0_0_rgba(15,23,42,0.06)]">
                          {vest.fullyVested ? (
                            <span className="text-xs font-normal italic text-gray-500">
                              — Award fully vested prior to CIC
                            </span>
                          ) : (
                            <span>{fmt(vest.parachuteValue)}</span>
                          )}
                        </td>
                        <td className="sticky right-0 z-10 w-[104px] min-w-[104px] bg-white px-2 py-2 group-hover:bg-gray-50">
                          <div className="inline-flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditModal(ind.id, award)}
                              className={workflowTableIconButtonEditClass}
                              title="Edit award"
                              aria-label="Edit award"
                            >
                              <Pencil size={18} strokeWidth={2} aria-hidden />
                            </button>
                            <button
                              type="button"
                              onClick={() => requestRemoveAward(ind.id, award.id, ind.name, award)}
                              className={workflowTableIconButtonDangerClass}
                              title="Remove award"
                              aria-label="Remove award"
                            >
                              <Trash2 size={18} strokeWidth={2} aria-hidden />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
      {getAwardCount() === 0 && (
        <Card className="py-6 px-4 sm:px-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Award size={24} className="text-gray-300 shrink-0" />
              <p className="text-left text-xs text-gray-500">No equity awards added yet</p>
            </div>
            <Button variant="secondary" size="sm" className="shrink-0" onClick={openAddModal}>
              <Plus size={16} className="mr-1 shrink-0" aria-hidden /> Add First Award
            </Button>
          </div>
        </Card>
      )}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal}></div>
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col"
            style={{ maxHeight: 'calc(100vh - 64px)' }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-xs font-medium text-gray-900">
                {editMode ? 'Edit Equity Award' : 'Add Equity Award'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} aria-hidden />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <Select
                label="Individual"
                value={selectedIndividual}
                onChange={(e) => setSelectedIndividual(e.target.value)}
                disabled={editMode}
              >
                <option value="">Select individual...</option>
                {uniqueIndividuals.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                    {ind.title ? ` — ${ind.title}` : ''}
                  </option>
                ))}
              </Select>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Award Type"
                  value={awardForm.type}
                  onChange={(e) => setAwardForm((p) => ({ ...p, type: e.target.value }))}
                >
                  <option value="option">Stock Option</option>
                  <option value="rsu">RSU</option>
                  <option value="restricted_stock">Restricted Stock</option>
                </Select>
                <Select
                  label="Vesting Type"
                  value={awardForm.vestingType}
                  onChange={(e) => setAwardForm((p) => ({ ...p, vestingType: e.target.value }))}
                >
                  <option value="time">Time-Based</option>
                  <option value="performance">Performance-Based</option>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Total Shares"
                  type="number"
                  value={awardForm.totalShares}
                  onChange={(e) => setAwardForm((p) => ({ ...p, totalShares: e.target.value }))}
                  placeholder="0"
                />
                {awardForm.type === 'option' && (
                  <Input
                    label="Exercise Price"
                    type="number"
                    step="0.01"
                    value={awardForm.exercisePrice}
                    onChange={(e) => setAwardForm((p) => ({ ...p, exercisePrice: e.target.value }))}
                    placeholder="0.00"
                  />
                )}
              </div>
              <WorkflowDateField
                label="Grant Date"
                value={awardForm.grantDate}
                onChange={(v) => setAwardForm((p) => ({ ...p, grantDate: v }))}
              />
              <Select
                label="Vesting Schedule"
                value={awardForm.vestingSchedule}
                onChange={(e) => setAwardForm((p) => ({ ...p, vestingSchedule: e.target.value }))}
              >
                {VESTING_SCHEDULES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </Select>
              {awardForm.vestingSchedule === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Vesting Months"
                    type="number"
                    value={awardForm.customVestingMonths}
                    onChange={(e) => setAwardForm((p) => ({ ...p, customVestingMonths: e.target.value }))}
                    placeholder="48"
                  />
                  <Input
                    label="Cliff Months"
                    type="number"
                    value={awardForm.customCliffMonths}
                    onChange={(e) => setAwardForm((p) => ({ ...p, customCliffMonths: e.target.value }))}
                    placeholder="12"
                  />
                </div>
              )}
              <Select
                label="Acceleration on CIC"
                value={awardForm.accelerationType}
                onChange={(e) => setAwardForm((p) => ({ ...p, accelerationType: e.target.value }))}
              >
                <option value="full">Full (100% of unvested)</option>
                <option value="partial">Partial (specify %)</option>
                <option value="none">None</option>
              </Select>
              {awardForm.accelerationType === 'partial' && (
                <Input
                  label="Acceleration %"
                  type="number"
                  min="1"
                  max="100"
                  value={awardForm.accelerationPercentage}
                  onChange={(e) => setAwardForm((p) => ({ ...p, accelerationPercentage: e.target.value }))}
                  placeholder="50"
                />
              )}
              <Select
                label="Treatment on CIC"
                value={awardForm.treatmentOnCIC || 'cashedOut'}
                onChange={(e) => setAwardForm((p) => ({ ...p, treatmentOnCIC: e.target.value }))}
              >
                <option value="cashedOut">Cashed out</option>
                <option value="rolledOver">Rolled over</option>
                <option value="assumed">Assumed</option>
              </Select>
            </div>
            <div className="flex justify-end space-x-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <Button variant="secondary" size="sm" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveAward}
                disabled={!selectedIndividual || !awardForm.totalShares || !awardForm.grantDate}
              >
                {editMode ? 'Save Changes' : 'Add Award'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!awardDeleteConfirm}
        title="Remove Equity Award"
        description={
          awardDeleteConfirm
            ? `Remove this ${awardDeleteConfirm.typeLabel} award for ${awardDeleteConfirm.individualName}? This cannot be undone.`
            : undefined
        }
        confirmLabel="Remove Award"
        onCancel={() => setAwardDeleteConfirm(null)}
        onConfirm={confirmRemoveAward}
      >
        {awardDeleteConfirm?.sharesLine || awardDeleteConfirm?.grantDate ? (
          <ul className="space-y-1.5 rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs text-gray-700">
            {awardDeleteConfirm.sharesLine ? (
              <li>
                <span className="text-gray-500">Shares: </span>
                <span className="font-medium">{awardDeleteConfirm.sharesLine}</span>
              </li>
            ) : null}
            {awardDeleteConfirm.grantDate ? (
              <li>
                <span className="text-gray-500">Grant date: </span>
                <span className="font-medium tabular-nums">{awardDeleteConfirm.grantDate}</span>
              </li>
            ) : null}
          </ul>
        ) : null}
      </ConfirmDialog>
    </div>
  );
};
